import type { FootnoteActionPayload, FootnotesActionPayload, LocalFootnoteStoreState } from './local-footnotes.types';

export const addFootnote = (
  state: LocalFootnoteStoreState,
  { text, nonIndexed, context }: FootnoteActionPayload,
): LocalFootnoteStoreState => {
  return addFootnotes(state, { context, footnotes: [{ text, nonIndexed }] });
};

export const addFootnotes = (
  state: LocalFootnoteStoreState,
  { footnotes, context }: FootnotesActionPayload,
): LocalFootnoteStoreState => {
  const targetContext = context ?? 'default';

  const stateContexts = { ...state.contexts };

  const stateFootnotes = stateContexts[targetContext] ?? [];

  footnotes.forEach((footnote) => {
    const nonIndexedFootnotes = stateFootnotes.filter((stateFootnote) => stateFootnote.referenceNumber === 0);
    let existingFootnote = stateFootnotes.find((stateFootnote) => stateFootnote.text === footnote.text);

    if (!existingFootnote) {
      // Non-indexed footnotes will be provided with reference number '0'
      const referenceNumber = footnote.nonIndexed ? 0 : stateFootnotes.length - nonIndexedFootnotes.length + 1;

      existingFootnote = {
        text: footnote.text,
        referenceCounter: 0,
        referenceNumber,
      };

      stateFootnotes.push(existingFootnote);
    }

    existingFootnote.referenceCounter += 1;
  });

  // Always sort footnotes before sending them back to store
  stateFootnotes.sort((a, b) => a.referenceNumber - b.referenceNumber);

  return {
    contexts: {
      ...stateContexts,
      [targetContext]: stateFootnotes,
    },
  };
};

export const removeFootnote = (
  state: LocalFootnoteStoreState,
  { text, nonIndexed, context }: FootnoteActionPayload,
): LocalFootnoteStoreState => {
  return removeFootnotes(state, { context, footnotes: [{ text, nonIndexed }] });
};

export const removeFootnotes = (
  state: LocalFootnoteStoreState,
  { context, footnotes }: FootnotesActionPayload,
): LocalFootnoteStoreState => {
  const targetContext = context ?? 'default';

  const stateContexts = { ...state.contexts };

  let stateFootnotes = stateContexts[targetContext] ?? [];

  footnotes.forEach((footnote) => {
    const existingFootnote = stateFootnotes.find((stateFootnote) => stateFootnote.text === footnote.text);

    if (!existingFootnote) return;

    existingFootnote.referenceCounter -= 1;

    // No more references to this footnote, remove it and update reference numbers
    if (existingFootnote.referenceCounter === 0) {
      let referenceNumber = 0;

      const incrementReferenceNumber = () => {
        return ++referenceNumber;
      };

      stateFootnotes = stateFootnotes
        .filter((stateFootnote) => stateFootnote.text !== footnote.text)
        .map((stateFootnote) => ({
          ...stateFootnote,
          // Increase a reference number only in case of an indexed footnote
          referenceNumber: stateFootnote.referenceNumber === 0 ? 0 : incrementReferenceNumber(),
        }));
    }
  });

  // Always sort footnotes before sending them back to store
  stateFootnotes.sort((a, b) => a.referenceNumber - b.referenceNumber);

  return {
    contexts: {
      ...stateContexts,
      [targetContext]: stateFootnotes,
    },
  };
};
